import { functions, isEqual, omit } from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import config from '../../config';
import { mapColorScheme, useDisplayMode } from '../../theme/displayMode';
import "./Map.css";

function googleColorScheme(scheme) {
  const ColorScheme = window.google && window.google.maps && window.google.maps.ColorScheme;
  if (!ColorScheme) return scheme;
  return scheme === 'DARK' ? ColorScheme.DARK : ColorScheme.LIGHT;
}

function validPoints(links) {
  return (links || []).filter((link) => {
    const lat = link && link.coords && Number(link.coords.lat);
    const lng = link && link.coords && Number(link.coords.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) > 0.05 && Math.abs(lng) > 0.05;
  });
}

function clusterPoints(points) {
  if (points.length <= 2) return points;
  const lats = points.map((item) => item.coords.lat).sort((a, b) => a - b);
  const lngs = points.map((item) => item.coords.lng).sort((a, b) => a - b);
  const midLat = lats[Math.floor(lats.length / 2)];
  const midLng = lngs[Math.floor(lngs.length / 2)];
  const clustered = points.filter((item) => (
    Math.abs(item.coords.lat - midLat) <= 4.5 && Math.abs(item.coords.lng - midLng) <= 4.5
  ));
  return clustered.length >= Math.min(3, points.length) ? clustered : points;
}

function fitSites(map, links) {
  if (!map || !window.google) return false;
  const area = clusterPoints(validPoints(links));
  if (!area.length) return false;
  const bounds = new window.google.maps.LatLngBounds();
  area.forEach((link) => {
    bounds.extend(new window.google.maps.LatLng(link.coords.lat, link.coords.lng));
  });
  if (bounds.isEmpty()) return false;
  if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
    bounds.extend(new window.google.maps.LatLng(
      bounds.getNorthEast().lat() + 0.04,
      bounds.getNorthEast().lng() + 0.04
    ));
  }
  map.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 });
  window.google.maps.event.addListenerOnce(map, 'idle', () => {
    const zoom = map.getZoom();
    if (zoom > 14) map.setZoom(14);
  });
  return true;
}

function applyOptionsCamera(map, options) {
  if (!map || !options) return;
  if (options.center) map.setCenter(options.center);
  if (options.zoom) map.setZoom(options.zoom);
}

function Map({ options, onMount, className, onMountProps, frameProps, viewCommand, fitToMarkers }) {
  const ref = useRef();
  const [map, setMap] = useState();
  const { theme, viewMode } = useDisplayMode();
  const onMountRef = useRef(onMount);
  const frameRef = useRef(frameProps || onMountProps);
  const optionsRef = useRef(options);
  const fittedRef = useRef(false);
  const laidOutRef = useRef(false);
  const appliedSchemeRef = useRef(null);
  const schemeKeyRef = useRef('LIGHT');
  const createMapRef = useRef();
  onMountRef.current = onMount;
  frameRef.current = frameProps || onMountProps;
  optionsRef.current = options;
  const schemeKey = mapColorScheme(theme, viewMode);
  schemeKeyRef.current = schemeKey;
  createMapRef.current = (camera) => {
    const opts = { ...optionsRef.current, ...(camera || {}) };
    opts.colorScheme = googleColorScheme(schemeKeyRef.current);
    appliedSchemeRef.current = schemeKeyRef.current;
    return new window.google.maps.Map(ref.current, opts);
  };
  const commandMode = viewCommand && viewCommand.mode;
  const commandSeq = viewCommand && viewCommand.seq;
  const centerLat = options && options.center && options.center.lat;
  const centerLng = options && options.center && options.center.lng;
  const optionZoom = options && options.zoom;

  useEffect(() => {
    let script;
    const onLoad = () => {
      if (!ref.current || !window.google) return;
      setMap(createMapRef.current());
    };
    if (!window.google) {
      script = document.createElement('script');
      script.src =
        `https://maps.googleapis.com/maps/api/js?key=` +
        config.googleMaps.GOOGLE_MAPS_API_KEY;
      document.head.append(script);
      script.addEventListener('load', onLoad);
      return () => script.removeEventListener('load', onLoad);
    }
    onLoad();
  }, []);

  useEffect(() => {
    if (!map || !ref.current || !window.google) return undefined;
    if (appliedSchemeRef.current === schemeKey) return undefined;
    const center = map.getCenter();
    const zoom = map.getZoom();
    setMap(createMapRef.current({
      center: center ? { lat: center.lat(), lng: center.lng() } : optionsRef.current.center,
      zoom: zoom || optionsRef.current.zoom,
    }));
    return undefined;
  }, [map, schemeKey]);

  useEffect(() => {
    if (!map || !ref.current || !window.google) return undefined;
    const applyLayout = () => {
      const el = ref.current;
      if (!el || el.offsetWidth < 8 || el.offsetHeight < 8) return;
      window.google.maps.event.trigger(map, 'resize');
      if (laidOutRef.current) return;
      laidOutRef.current = true;
      if (fitToMarkers || commandMode === 'sites') {
        if (fitSites(map, frameRef.current)) fittedRef.current = true;
      } else {
        applyOptionsCamera(map, optionsRef.current);
      }
    };
    applyLayout();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(applyLayout)
      : null;
    if (observer) observer.observe(ref.current);
    window.addEventListener('resize', applyLayout);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', applyLayout);
    };
  }, [map, fitToMarkers, commandMode]);

  useEffect(() => {
    if (!map || typeof onMountRef.current !== 'function') return undefined;
    let cleanup;
    let cancelled = false;
    const result = onMountRef.current(map, onMountProps || []);
    if (result && typeof result.then === 'function') {
      result.then((fn) => {
        if (cancelled) {
          if (typeof fn === 'function') fn();
          return;
        }
        cleanup = fn;
      });
    } else {
      cleanup = result;
    }
    return () => {
      cancelled = true;
      if (typeof cleanup === 'function') cleanup();
    };
  }, [map, onMountProps]);

  useEffect(() => {
    if (!map || fittedRef.current || commandMode === 'overview') return undefined;
    if (!(fitToMarkers || commandMode === 'sites')) return undefined;
    if (fitSites(map, frameRef.current)) fittedRef.current = true;
    return undefined;
  }, [map, onMountProps, frameProps, commandMode, fitToMarkers]);

  useEffect(() => {
    if (!map || !commandMode || !commandSeq) return undefined;
    if (commandMode === 'overview') {
      applyOptionsCamera(map, optionsRef.current);
      return undefined;
    }
    fitSites(map, frameRef.current);
    return undefined;
  }, [map, commandMode, commandSeq]);

  useEffect(() => {
    if (!map || commandMode || fitToMarkers) return undefined;
    if (!Number.isFinite(centerLat) || !Number.isFinite(centerLng)) return undefined;
    applyOptionsCamera(map, optionsRef.current);
    return undefined;
  }, [map, centerLat, centerLng, optionZoom, commandMode, fitToMarkers]);

  return (
    <div
      {...{ ref, className }}
    />
  );
}

function shouldNotUpdate(props, nextProps) {
  const [funcs, nextFuncs] = [functions(props), functions(nextProps)];
  const noPropChange = isEqual(omit(props, funcs), omit(nextProps, nextFuncs));
  const noFuncChange =
    funcs.length === nextFuncs.length &&
    funcs.every(fn => props[fn].toString() === nextProps[fn].toString());
  return noPropChange && noFuncChange;
}

export default React.memo(Map, shouldNotUpdate);

Map.defaultProps = {
  options: {
    center: { lat: 53.65, lng: -6.68 },
    zoom: 10,
    scrollwheel: true,
    id: 'map'
  },
};
